import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
	ChevronRight,
	Filter,
	Pause,
	Phone,
	PhoneOff,
	Play,
	SkipForward,
	Users,
	X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { crmApi, phoneApi, type ApiRecord } from '../../lib/api';
import { usePhone } from './PhoneProvider';

type DialEntry = {
	contactId: number;
	name: string;
	phone: string;
	companyName?: string;
	status: 'pending' | 'calling' | 'completed' | 'skipped' | 'failed' | 'no_answer';
	disposition?: string;
	callId?: string;
	duration?: number;
};

type DialerState = 'idle' | 'running' | 'paused' | 'done';

export function PowerDialerPage() {
	const { pbxEnabled, pbxHealthy, activeCall } = usePhone();

	// --- Contact list + filtering ---
	const [companyFilter, setCompanyFilter] = useState('');
	const [searchFilter, setSearchFilter] = useState('');

	const contactsQuery = useQuery({
		queryKey: ['contacts', 'dialer', companyFilter, searchFilter],
		queryFn: () =>
			crmApi.contacts({
				...(companyFilter ? { company_id: companyFilter } : {}),
				...(searchFilter ? { q: searchFilter } : {}),
				limit: '200'
			})
	});

	const companiesQuery = useQuery({
		queryKey: ['companies', 'dialer-options'],
		queryFn: () => crmApi.companies({ limit: '500' })
	});

	const contacts = useMemo(
		() => (Array.isArray(contactsQuery.data) ? contactsQuery.data : []),
		[contactsQuery.data]
	);

	const companies = useMemo(
		() => (Array.isArray(companiesQuery.data) ? companiesQuery.data : []),
		[companiesQuery.data]
	);

	// Only contacts with phone numbers
	const dialableContacts = useMemo(
		() => contacts.filter((c) => c.phone && String(c.phone).trim()),
		[contacts]
	);

	// --- Dial queue ---
	const [queue, setQueue] = useState<DialEntry[]>([]);
	const [dialerState, setDialerState] = useState<DialerState>('idle');
	const [currentIndex, setCurrentIndex] = useState(0);
	const [dispositionInput, setDispositionInput] = useState('');
	const pausedRef = useRef(false);
	const stoppedRef = useRef(false);

	const addToQueue = useCallback(
		(contact: ApiRecord) => {
			const id = Number(contact.id);
			if (queue.some((e) => e.contactId === id)) {
				toast.info('Contact already in dial list');
				return;
			}
			const name = `${String(contact.first_name ?? '')} ${String(contact.last_name ?? '')}`.trim() || String(contact.email ?? 'Unknown');
			const companyName = (contact.company as ApiRecord | undefined)?.name
				? String((contact.company as ApiRecord).name)
				: undefined;
			setQueue((prev) => [
				...prev,
				{
					contactId: id,
					name,
					phone: String(contact.phone ?? ''),
					companyName,
					status: 'pending'
				}
			]);
		},
		[queue]
	);

	const addAllDialable = useCallback(() => {
		const existingIds = new Set(queue.map((e) => e.contactId));
		const newEntries: DialEntry[] = dialableContacts
			.filter((c) => !existingIds.has(Number(c.id)))
			.map((c) => ({
				contactId: Number(c.id),
				name: `${String(c.first_name ?? '')} ${String(c.last_name ?? '')}`.trim() || String(c.email ?? 'Unknown'),
				phone: String(c.phone ?? ''),
				companyName: (c.company as ApiRecord | undefined)?.name
					? String((c.company as ApiRecord).name)
					: undefined,
				status: 'pending' as const
			}));
		if (newEntries.length === 0) {
			toast.info('No new contacts to add');
			return;
		}
		setQueue((prev) => [...prev, ...newEntries]);
		toast.success(`Added ${newEntries.length} contacts to dial list`);
	}, [queue, dialableContacts]);

	const removeFromQueue = useCallback(
		(contactId: number) => {
			if (dialerState === 'running') return;
			setQueue((prev) => prev.filter((e) => e.contactId !== contactId));
		},
		[dialerState]
	);

	const clearQueue = useCallback(() => {
		if (dialerState === 'running') return;
		setQueue([]);
		setCurrentIndex(0);
		setDialerState('idle');
	}, [dialerState]);

	// --- Auto-dial engine ---
	const dialNext = useCallback(
		async (index: number) => {
			if (stoppedRef.current) return;
			if (index >= queue.length) {
				setDialerState('done');
				toast.success('Dial list completed');
				return;
			}

			if (pausedRef.current) {
				setDialerState('paused');
				return;
			}

			setCurrentIndex(index);
			setDialerState('running');

			const entry = queue[index];
			if (entry.status !== 'pending') {
				// Already handled, move to next
				void dialNext(index + 1);
				return;
			}

			// Update status to calling
			setQueue((prev) =>
				prev.map((e, i) => (i === index ? { ...e, status: 'calling' } : e))
			);

			try {
				const result = await phoneApi.originate({
					to_number: entry.phone,
					contact_id: entry.contactId
				});
				setQueue((prev) =>
					prev.map((e, i) =>
						i === index
							? { ...e, callId: String(result.call?.call_id ?? result.channel_id ?? '') }
							: e
					)
				);
			} catch {
				setQueue((prev) =>
					prev.map((e, i) => (i === index ? { ...e, status: 'failed' } : e))
				);
				toast.error(`Failed to call ${entry.name}`);
				// Auto-advance after failure
				setTimeout(() => void dialNext(index + 1), 2000);
			}
		},
		[queue]
	);

	// Watch for active call ending to advance to the next entry
	const prevActiveCall = useRef(activeCall);
	useEffect(() => {
		const wasActive = prevActiveCall.current;
		prevActiveCall.current = activeCall;

		// Call just ended
		if (wasActive && !activeCall && dialerState === 'running') {
			const idx = currentIndex;
			setQueue((prev) =>
				prev.map((e, i) =>
					i === idx && e.status === 'calling' ? { ...e, status: 'completed' } : e
				)
			);
			// Small delay before next call to let the user breathe
			setTimeout(() => void dialNext(idx + 1), 3000);
		}
	}, [activeCall, dialerState, currentIndex, dialNext]);

	const startDialer = useCallback(() => {
		if (queue.length === 0) {
			toast.error('Add contacts to the dial list first');
			return;
		}
		pausedRef.current = false;
		stoppedRef.current = false;
		const startIdx = queue.findIndex((e) => e.status === 'pending');
		if (startIdx === -1) {
			toast.info('All contacts have been dialed');
			setDialerState('done');
			return;
		}
		void dialNext(startIdx);
	}, [queue, dialNext]);

	const pauseDialer = useCallback(() => {
		pausedRef.current = true;
		setDialerState('paused');
	}, []);

	const resumeDialer = useCallback(() => {
		pausedRef.current = false;
		const nextIdx = queue.findIndex((e, i) => i >= currentIndex && e.status === 'pending');
		if (nextIdx === -1) {
			setDialerState('done');
			return;
		}
		void dialNext(nextIdx);
	}, [queue, currentIndex, dialNext]);

	const stopDialer = useCallback(() => {
		stoppedRef.current = true;
		pausedRef.current = false;
		setDialerState('idle');
	}, []);

	const skipCurrent = useCallback(() => {
		const idx = currentIndex;
		setQueue((prev) =>
			prev.map((e, i) =>
				i === idx && (e.status === 'calling' || e.status === 'pending')
					? { ...e, status: 'skipped' }
					: e
			)
		);
		void dialNext(idx + 1);
	}, [currentIndex, dialNext]);

	const setDisposition = useCallback(
		(index: number, disposition: string) => {
			setQueue((prev) =>
				prev.map((e, i) => (i === index ? { ...e, disposition } : e))
			);

			// Update call record if we have a call ID
			const entry = queue[index];
			if (entry?.callId) {
				void phoneApi.updateCall(entry.callId, { disposition }).catch(() => {
					// Silent — best effort
				});
			}
		},
		[queue]
	);

	// --- Stats ---
	const stats = useMemo(() => {
		const total = queue.length;
		const completed = queue.filter((e) => e.status === 'completed').length;
		const skipped = queue.filter((e) => e.status === 'skipped').length;
		const failed = queue.filter((e) => e.status === 'failed').length;
		const pending = queue.filter((e) => e.status === 'pending').length;
		return { total, completed, skipped, failed, pending };
	}, [queue]);

	const currentEntry = queue[currentIndex];

	if (!pbxEnabled) {
		return (
			<div className="space-y-6">
				<section className="rounded-[2rem] border border-subtle bg-gradient-to-br from-surface to-surface-2 p-8 shadow-panel backdrop-blur-xl">
					<h1 className="text-4xl font-semibold text-primary">Power Dialer</h1>
				</section>
				<div className="rounded-3xl border border-subtle bg-surface-2 p-8 text-center text-muted">
					Phone system is not enabled. Configure PBX settings to use the power dialer.
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<section className="rounded-[2rem] border border-subtle bg-gradient-to-br from-surface to-surface-2 p-8 shadow-panel backdrop-blur-xl">
				<div className="flex items-center justify-between">
					<h1 className="text-4xl font-semibold text-primary">Power Dialer</h1>
					<div className="flex items-center gap-2">
						<span
							className={cn(
								'h-2.5 w-2.5 rounded-full',
								pbxHealthy ? 'bg-green-400' : 'bg-red-400'
							)}
						/>
						<span className="text-sm text-muted">
							{pbxHealthy ? 'PBX connected' : 'PBX disconnected'}
						</span>
					</div>
				</div>
			</section>

			<div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(380px,1fr)]">
				{/* Left: Contact selector */}
				<div className="space-y-4">
					<div className="rounded-3xl border border-subtle bg-surface-2 p-5">
						<div className="mb-4 flex items-center justify-between">
							<h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-muted">
								<Users className="h-4 w-4" />
								Contacts
							</h2>
							<button
								onClick={addAllDialable}
								disabled={dialerState === 'running'}
								className="rounded-xl bg-accent/15 px-3 py-1.5 text-xs font-medium text-accent transition hover:bg-accent/30 disabled:opacity-40"
							>
								Add all with phone
							</button>
						</div>

						{/* Filters */}
						<div className="mb-4 flex gap-2">
							<input
								value={searchFilter}
								onChange={(e) => setSearchFilter(e.target.value)}
								placeholder="Search contacts..."
								className="flex-1 rounded-2xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none"
							/>
							<select
								value={companyFilter}
								onChange={(e) => setCompanyFilter(e.target.value)}
								className="rounded-2xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary"
							>
								<option value="">All companies</option>
								{companies.map((c) => (
									<option key={String(c.id)} value={String(c.id)}>
										{String(c.name ?? c.id)}
									</option>
								))}
							</select>
						</div>

						{/* Contact list */}
						<div className="max-h-[500px] space-y-1 overflow-y-auto">
							{contactsQuery.isLoading ? (
								<p className="py-4 text-center text-sm text-muted">Loading contacts...</p>
							) : dialableContacts.length > 0 ? (
								dialableContacts.map((contact) => {
									const inQueue = queue.some((e) => e.contactId === Number(contact.id));
									const name = `${String(contact.first_name ?? '')} ${String(contact.last_name ?? '')}`.trim() || String(contact.email ?? 'Unknown');
									return (
										<div
											key={String(contact.id)}
											className={cn(
												'flex items-center justify-between rounded-2xl border px-3 py-2.5 transition',
												inQueue
													? 'border-accent/30 bg-accent/5'
													: 'border-subtle bg-white/[0.02] hover:bg-surface-2'
											)}
										>
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium text-primary">{name}</p>
												<p className="truncate text-xs text-muted">
													{String(contact.phone ?? '')}
													{(contact.company as ApiRecord | undefined)?.name
														? ` · ${String((contact.company as ApiRecord).name)}`
														: ''}
												</p>
											</div>
											{inQueue ? (
												<span className="shrink-0 text-[10px] font-medium uppercase text-accent">In list</span>
											) : (
												<button
													onClick={() => addToQueue(contact)}
													disabled={dialerState === 'running'}
													className="shrink-0 rounded-lg p-1.5 text-muted transition hover:bg-surface-hover hover:text-primary disabled:opacity-40"
												>
													<ChevronRight className="h-4 w-4" />
												</button>
											)}
										</div>
									);
								})
							) : (
								<p className="py-4 text-center text-sm text-muted">
									{contacts.length > 0 ? 'No contacts with phone numbers.' : 'No contacts found.'}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Right: Dial queue + controls */}
				<div className="space-y-4">
					{/* Controls */}
					<div className="rounded-3xl border border-subtle bg-surface-2 p-5">
						<div className="mb-4 flex items-center justify-between">
							<h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-muted">
								<Phone className="h-4 w-4" />
								Dial list ({queue.length})
							</h2>
							<div className="flex items-center gap-2">
								{dialerState === 'idle' || dialerState === 'done' ? (
									<>
										<button
											onClick={startDialer}
											disabled={queue.length === 0 || !pbxHealthy}
											className="flex items-center gap-1.5 rounded-xl bg-green-600 px-4 py-2 text-xs font-semibold text-primary transition hover:bg-green-500 disabled:opacity-40"
										>
											<Play className="h-3.5 w-3.5" />
											Start
										</button>
										<button
											onClick={clearQueue}
											disabled={queue.length === 0}
											className="flex items-center gap-1.5 rounded-xl border border-subtle px-3 py-2 text-xs text-secondary transition hover:bg-surface-hover disabled:opacity-40"
										>
											<X className="h-3.5 w-3.5" />
											Clear
										</button>
									</>
								) : dialerState === 'running' ? (
									<>
										<button
											onClick={skipCurrent}
											className="flex items-center gap-1.5 rounded-xl border border-subtle px-3 py-2 text-xs text-secondary transition hover:bg-surface-hover"
										>
											<SkipForward className="h-3.5 w-3.5" />
											Skip
										</button>
										<button
											onClick={pauseDialer}
											className="flex items-center gap-1.5 rounded-xl bg-amber-600 px-4 py-2 text-xs font-semibold text-primary transition hover:bg-amber-500"
										>
											<Pause className="h-3.5 w-3.5" />
											Pause
										</button>
										<button
											onClick={stopDialer}
											className="flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-primary transition hover:bg-red-500"
										>
											<PhoneOff className="h-3.5 w-3.5" />
											Stop
										</button>
									</>
								) : dialerState === 'paused' ? (
									<>
										<button
											onClick={resumeDialer}
											className="flex items-center gap-1.5 rounded-xl bg-green-600 px-4 py-2 text-xs font-semibold text-primary transition hover:bg-green-500"
										>
											<Play className="h-3.5 w-3.5" />
											Resume
										</button>
										<button
											onClick={stopDialer}
											className="flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-primary transition hover:bg-red-500"
										>
											<PhoneOff className="h-3.5 w-3.5" />
											Stop
										</button>
									</>
								) : null}
							</div>
						</div>

						{/* Progress bar */}
						{queue.length > 0 && (
							<div className="mb-4">
								<div className="mb-2 flex justify-between text-xs text-muted">
									<span>{stats.completed + stats.skipped + stats.failed} of {stats.total} processed</span>
									<span>{stats.pending} remaining</span>
								</div>
								<div className="h-2 overflow-hidden rounded-full bg-surface-hover">
									<div
										className="h-full rounded-full bg-gradient-to-r from-green-500 to-accent transition-all duration-500"
										style={{
											width: `${stats.total > 0 ? ((stats.completed + stats.skipped + stats.failed) / stats.total) * 100 : 0}%`
										}}
									/>
								</div>
								<div className="mt-2 flex gap-4 text-[11px]">
									<span className="text-success">{stats.completed} completed</span>
									<span className="text-amber-400">{stats.skipped} skipped</span>
									<span className="text-danger">{stats.failed} failed</span>
								</div>
							</div>
						)}

						{/* Current call card */}
						{currentEntry && dialerState === 'running' && currentEntry.status === 'calling' && (
							<div className="mb-4 rounded-2xl border border-green-400/20 bg-green-400/5 p-4">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15">
										<Phone className="h-5 w-5 animate-pulse text-success" />
									</div>
									<div className="min-w-0 flex-1">
										<p className="font-medium text-primary">{currentEntry.name}</p>
										<p className="text-xs text-muted">
											{currentEntry.phone}
											{currentEntry.companyName ? ` · ${currentEntry.companyName}` : ''}
										</p>
									</div>
								</div>
								<div className="mt-3 flex items-center gap-2">
									<input
										value={dispositionInput}
										onChange={(e) => setDispositionInput(e.target.value)}
										placeholder="Disposition / notes..."
										className="flex-1 rounded-xl border border-subtle bg-canvas px-3 py-2 text-xs text-primary placeholder:text-muted focus:outline-none"
									/>
									<button
										onClick={() => {
											if (dispositionInput.trim()) {
												setDisposition(currentIndex, dispositionInput.trim());
												setDispositionInput('');
												toast.success('Disposition saved');
											}
										}}
										className="rounded-xl bg-surface-hover px-3 py-2 text-xs text-primary transition hover:bg-surface-hover"
									>
										Save
									</button>
								</div>
							</div>
						)}

						{/* Queue list */}
						<div className="max-h-[400px] space-y-1 overflow-y-auto">
							{queue.length > 0 ? (
								queue.map((entry, index) => {
									const statusBadge = {
										pending: { color: 'bg-slate-500/20 text-muted', label: 'Pending' },
										calling: { color: 'bg-success/15 text-success', label: 'Calling' },
										completed: { color: 'bg-success/15 text-success', label: 'Done' },
										skipped: { color: 'bg-amber-500/20 text-amber-400', label: 'Skipped' },
										failed: { color: 'bg-danger/15 text-danger', label: 'Failed' },
										no_answer: { color: 'bg-danger/15 text-danger', label: 'No answer' }
									}[entry.status];

									return (
										<div
											key={entry.contactId}
											className={cn(
												'flex items-center gap-3 rounded-2xl border px-3 py-2.5',
												index === currentIndex && dialerState === 'running'
													? 'border-green-400/20 bg-green-400/5'
													: 'border-subtle bg-white/[0.02]'
											)}
										>
											<span className="w-6 text-center text-xs tabular-nums text-muted">{index + 1}</span>
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium text-primary">{entry.name}</p>
												<p className="truncate text-xs text-muted">
													{entry.phone}
													{entry.disposition ? ` · ${entry.disposition}` : ''}
												</p>
											</div>
											<span className={cn('shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-medium', statusBadge.color)}>
												{statusBadge.label}
											</span>
											{dialerState !== 'running' && entry.status === 'pending' && (
												<button
													onClick={() => removeFromQueue(entry.contactId)}
													className="shrink-0 rounded-lg p-1 text-muted transition hover:text-danger"
												>
													<X className="h-3.5 w-3.5" />
												</button>
											)}
										</div>
									);
								})
							) : (
								<p className="py-8 text-center text-sm text-muted">
									Select contacts from the left panel to build your dial list.
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
